import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { applyTheme } from "@/context/ThemeProvider";
import { setFavicon } from "@/lib/favicon";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/button";
import { Bike, Loader2, ShieldCheck, MapPin, IndianRupee, ArrowRight, CircleDot } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;
const SCOOTER = "https://images.unsplash.com/photo-1597260491619-bab87197869f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxlbGVjdHJpYyUyMHNjb290ZXIlMjByZW50YWwlMjBjaXR5fGVufDB8fHx8MTc4MTIxNTUyOXww&ixlib=rb-4.1.0&q=85";

const TRUST = [
  { icon: ShieldCheck, title: "KYC-verified riders", body: "Every rider passes ID, selfie and reference checks before a key changes hands." },
  { icon: MapPin, title: "Live GPS-tracked", body: "Each vehicle is tracked in real time with geofence safety and recovery built in." },
  { icon: IndianRupee, title: "Transparent UPI pricing", body: "Pay by UPI with clear deposits and zero hidden gateway fees." },
];

export default function OperatorLanding() {
  const { subdomain } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useDocumentTitle(data ? `${data.name} — Book an electric ride` : "Rentals", data ? true : false);

  useEffect(() => {
    axios.get(`${API}/public/operators/${subdomain}/landing`)
      .then(({ data }) => {
        setData(data);
        applyTheme(data.branding?.tokens);
        setFavicon({ logoUrl: data.logo_url, name: data.branding?.logo_text || data.name, primary: data.branding?.tokens?.["--tenant-primary"] });
      })
      .catch(() => setError("We couldn't find this operator."));
  }, [subdomain]);

  if (error) return <Centered>{error}</Centered>;
  if (!data) return <Centered><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></Centered>;

  const b = data.branding || {};
  const canBook = data.accepting_bookings;
  const logoSrc = data.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${data.logo_url}` : null;

  const Mark = ({ size = "h-9 w-9", icon = "h-5 w-5" }) => (
    logoSrc
      ? <img src={logoSrc} alt={data.name} className={`${size} rounded-[12px] object-contain bg-tenant-surface`} />
      : <div className={`flex ${size} items-center justify-center rounded-[12px] bg-tenant-primary text-tenant-primary-foreground`}><Bike className={icon} /></div>
  );

  return (
    <div data-testid="operator-landing" className="min-h-screen bg-tenant-bg text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-tenant-border bg-tenant-surface/80 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <Mark />
            <span className="font-bold tracking-tight">{b.logo_text || data.name}</span>
          </div>
          <Link to="/login" data-testid="operator-login" className="text-sm font-medium text-muted-foreground hover:text-foreground">Staff login</Link>
        </div>
      </header>

      {/* Branded hero */}
      <section className="mx-auto grid w-full max-w-6xl items-center gap-10 px-6 py-16 lg:grid-cols-2 lg:py-24">
        <div className="animate-fade-up">
          {data.stats.available > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-tenant-primary/10 px-3 py-1 text-xs font-semibold text-tenant-primary">
              <CircleDot className="h-3.5 w-3.5 animate-pulse" /> {data.stats.available} {data.stats.available === 1 ? "bike" : "bikes"} ready to ride
            </span>
          )}
          <h1 className="mt-4 font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{data.name}</h1>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">{b.tagline || "Ride electric. Book in minutes, pick up today — KYC-verified and GPS-tracked."}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {canBook ? (
              <Button asChild size="lg" className="rounded-full bg-tenant-primary px-8 text-tenant-primary-foreground hover:bg-tenant-primary/90">
                <Link to={`/book/${subdomain}`} data-testid="hero-book-cta">Book now <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
            ) : (
              <Button size="lg" disabled className="rounded-full px-8" data-testid="hero-book-disabled">Online booking coming soon</Button>
            )}
          </div>
          <div className="mt-8 flex gap-8 text-sm">
            <div><p className="text-2xl font-bold tracking-tight">{data.stats.fleet_size}</p><p className="text-muted-foreground">in the fleet</p></div>
            <div><p className="text-2xl font-bold tracking-tight">{data.stats.models}</p><p className="text-muted-foreground">models</p></div>
            <div><p className="text-2xl font-bold tracking-tight text-tenant-primary">{data.stats.available}</p><p className="text-muted-foreground">available now</p></div>
          </div>
        </div>
        <div className="animate-fade-up">
          <div className="surface-tactile overflow-hidden rounded-[28px]"><img src={SCOOTER} alt="Electric scooter" className="h-full w-full object-cover" loading="lazy" /></div>
        </div>
      </section>

      {/* Available models */}
      {data.models.length > 0 && (
        <section className="mx-auto w-full max-w-6xl px-6 py-16">
          <h2 className="font-bold tracking-tight text-2xl sm:text-3xl">Available models</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.models.map((m) => (
              <div key={m.model} data-testid={`model-${m.model}`} className="surface-tactile hover-lift rounded-[24px] p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-tenant-primary/10 text-tenant-primary"><Bike className="h-6 w-6" /></div>
                <h3 className="mt-4 font-bold tracking-tight text-xl">{m.model}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{m.count} available{m.from_deposit ? ` · deposit from ${inr(m.from_deposit)}` : ""}</p>
                {canBook && (
                  <Link to={`/book/${subdomain}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-tenant-primary hover:underline">Book this <ArrowRight className="h-3.5 w-3.5" /></Link>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Trust */}
      <section className="mx-auto w-full max-w-6xl px-6 py-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {TRUST.map((tp) => (
            <div key={tp.title} className="surface-tactile rounded-[24px] p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-[14px] bg-tenant-primary/10 text-tenant-primary"><tp.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-bold tracking-tight text-lg">{tp.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{tp.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-tenant-border bg-tenant-surface">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-4 px-6 py-10 sm:flex-row">
          <div className="flex items-center gap-2.5"><Mark size="h-8 w-8" icon="h-4 w-4" /><span className="font-semibold">{b.logo_text || data.name}</span></div>
          {canBook && <Button asChild className="rounded-full bg-tenant-primary text-tenant-primary-foreground hover:bg-tenant-primary/90"><Link to={`/book/${subdomain}`} data-testid="footer-book-cta">Book now</Link></Button>}
        </div>
        <p className="pb-6 text-center text-xs text-muted-foreground">Powered by FleetOS by Graphikpro</p>
      </footer>
    </div>
  );
}

function Centered({ children }) {
  return <div className="flex min-h-screen items-center justify-center bg-tenant-bg p-4 text-center text-muted-foreground">{children}</div>;
}
